
import { useState, useCallback } from 'react';
import { BlogPost } from '@/types/blog';
import { fetchBlogPosts } from './utils/fetchUtils';
import { getPerPage } from './utils/paginationUtils';
import { clearBlogFiltersCache } from './utils/cacheUtils';

interface UseFetchPostsProps {
  initialPage: number;
  initialPerPage: number;
  subsequentPerPage?: number;
  currentCategory: string | number;
  currentSearchTerm: string;
}

interface UseFetchPostsReturn {
  loadPosts: (page: number, replace?: boolean, forceRefresh?: boolean) => Promise<BlogPost[]>;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  fetchInProgress: boolean;
  lastFetchTime: number;
}

export const useFetchPosts = ({
  initialPage,
  initialPerPage,
  subsequentPerPage,
  currentCategory,
  currentSearchTerm,
}: UseFetchPostsProps): UseFetchPostsReturn => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchInProgress, setFetchInProgress] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  
  // Funzione semplificata per caricare i post
  const loadPosts = useCallback(async (page: number, replace = false, forceRefresh = false): Promise<BlogPost[]> => {
    // Resetta lo stato di fetch in corso all'inizio di ogni chiamata
    setFetchInProgress(false);
    
    // Imposta lo stato di caricamento
    setFetchInProgress(true);
    
    if (replace) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    setError(null);
    
    try {
      // Calcola il numero di post per pagina
      const perPage = getPerPage(page, initialPage, initialPerPage, subsequentPerPage);
      
      // Forza una pulizia completa della cache quando cambia la categoria
      if (typeof window !== 'undefined') {
        console.log('[useFetchPosts] Forzando pulizia completa della cache');
        // Importa la funzione per forzare la pulizia completa della cache
        const { forceFullCacheClear } = await import('./utils/cacheUtils');
        forceFullCacheClear();
      }
      
      // Esegui la richiesta API
      // Assicurati che la categoria sia passata correttamente
      const categoryParam = currentCategory !== null && currentCategory !== '' ? 
        (typeof currentCategory === 'string' ? parseInt(currentCategory, 10) : currentCategory) : 
        undefined;
      
      console.log(`[useFetchPosts] Parametro categoria per API: ${categoryParam} (tipo: ${typeof categoryParam})`);
      
      const newPosts = await fetchBlogPosts(
        page, 
        perPage, 
        currentSearchTerm, 
        categoryParam,
        true // Sempre forza refresh
      );
      
      console.log(`[useFetchPosts] Ricevuti ${newPosts.length} post dall'API`);
      
      // Aggiorna il timestamp dell'ultimo fetch
      setLastFetchTime(Date.now());
      
      return newPosts;
    } catch (err) {
      console.error("[useFetchPosts] Errore caricamento post:", err);
      setError('Errore nel caricamento dei post');
      return [];
    } finally {
      // Resetta gli stati di caricamento
      setLoading(false);
      setLoadingMore(false);
      setFetchInProgress(false);
    }
  }, [initialPage, initialPerPage, subsequentPerPage, currentSearchTerm, currentCategory, fetchInProgress]);

  return {
    loadPosts,
    loading,
    loadingMore,
    error,
    fetchInProgress,
    lastFetchTime
  };
};
